/**
 * Parses `EdgeInstance.reported.doors` — the axema_dcip edge worker's
 * per-door health, carried in the appliance `status` frame
 * (`{"<door_id>": {"used", "capacity", "unplaced", "pending_ops", "stuck",
 * "beyond_capacity"}}`). `reported` stays an untyped dict end to end (the
 * API keeps it that way; see api/docs/superpowers/plans/2026-09-26-f47.md
 * Task 2), and an old agent omits `doors` (or, for older agents still,
 * `beyond_capacity`) entirely — so every read here is defensive: a missing
 * key, a non-object value, or a non-numeric field renders as unknown (null)
 * rather than throwing.
 */

export interface DoorHealthRow {
  doorId: string
  /** From the instance's own `params.doors` bindings (DoorMapping entries —
   *  edge/edge/workers/axema_dcip/directory.py) when one carries a
   *  non-empty `name` for this door id; null shows the id alone. */
  name: string | null
  used: number | null
  capacity: number | null
  unplaced: number | null
  pendingOps: number | null
  stuck: number | null
  /** Ledger records at indexes at/past the door's configured capacity — the
   *  bridge no longer manages these until `record_capacity` is fixed, and a
   *  revoked person among them may still have access. Missing on older
   *  agents (tolerated: treated as unknown, not zero). */
  beyondCapacity: number | null
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** door_id -> name, from the instance's `params.doors` list (each entry is
 *  `{"door_id"|"id": ..., "name": ..., ...}`; unnamed/malformed entries are
 *  skipped rather than failing the whole lookup). */
function doorNamesFromParams(params: Record<string, unknown> | null | undefined): Map<string, string> {
  const names = new Map<string, string>()
  const doors = params?.doors
  if (!Array.isArray(doors)) return names
  for (const entry of doors) {
    if (!isPlainObject(entry)) continue
    const id = entry.door_id ?? entry.id
    const name = entry.name
    if ((typeof id === 'string' || typeof id === 'number') && typeof name === 'string' && name.trim() !== '') {
      names.set(String(id), name)
    }
  }
  return names
}

/**
 * Returns the per-door rows from `reported.doors`, sorted for a stable
 * display order — or `null` when the key is missing or not a plain object
 * (old agents / non-DCIP instances): callers render nothing in that case.
 * An empty `{}` (a DCIP instance with no configured doors) yields `[]`.
 */
export function parseDoorHealth(
  reported: Record<string, unknown> | null | undefined,
  params?: Record<string, unknown> | null,
): DoorHealthRow[] | null {
  const doors = reported?.doors
  if (!isPlainObject(doors)) return null

  const names = doorNamesFromParams(params)
  const rows: DoorHealthRow[] = Object.entries(doors).map(([doorId, raw]) => {
    const v = isPlainObject(raw) ? raw : {}
    return {
      doorId,
      name: names.get(doorId) ?? null,
      used: asFiniteNumber(v.used),
      capacity: asFiniteNumber(v.capacity),
      unplaced: asFiniteNumber(v.unplaced),
      pendingOps: asFiniteNumber(v.pending_ops),
      stuck: asFiniteNumber(v.stuck),
      beyondCapacity: asFiniteNumber(v.beyond_capacity),
    }
  })
  rows.sort((a, b) => (a.name ?? a.doorId).localeCompare(b.name ?? b.doorId))
  return rows
}

/** "Full": unplaced > 0 — the badge label shows the count. */
export function isFull(row: DoorHealthRow): boolean {
  return (row.unplaced ?? 0) > 0
}

/** "Stuck": ops given up and awaiting backoff. */
export function isStuck(row: DoorHealthRow): boolean {
  return (row.stuck ?? 0) > 0
}

/** "Beyond capacity": ledger records at/past the door's configured
 *  capacity — unmanaged until `record_capacity` is fixed; a revoked
 *  person among them may still have access. Missing on older agents,
 *  which is tolerated (no badge, not an error). */
export function isBeyondCapacity(row: DoorHealthRow): boolean {
  return (row.beyondCapacity ?? 0) > 0
}

/** used/capacity >= 90%; undefined/zero/negative capacity never counts as
 *  near-full (nothing to divide by). */
export function isNearFull(row: DoorHealthRow): boolean {
  return row.used !== null && row.capacity !== null && row.capacity > 0 && row.used / row.capacity >= 0.9
}

/** 0..100+ (uncapped — a door can exceed its configured capacity), or null
 *  when used/capacity aren't both known numbers or capacity isn't positive.
 *  A negative `used` (odd/malformed telemetry) is treated as 0 rather than
 *  producing a negative percentage. */
export function usagePct(row: DoorHealthRow): number | null {
  if (row.used === null || row.capacity === null || row.capacity <= 0) return null
  return (Math.max(0, row.used) / row.capacity) * 100
}
